/**
 * Audio-Engine: AudioContext-Graph + Echtzeit-Mischung.
 *
 * Die Klangquelle ist KEIN Sample, sondern ein prozeduraler AudioWorklet
 * (siehe engine-processor.js). Auf lokalen IPs ohne sicheren Browser-Kontext
 * fällt die Engine auf ScriptProcessor zurück, damit der Speaker trotzdem
 * einen vollwertigen Motorstrom erhält.
 * Sein Roh-Signal läuft durch denselben Multiband-Split wie zuvor, sodass
 * Mixer, RPM-Bänder, AirFix und Events unverändert weiterarbeiten.
 *
 * Signalfluss:
 *   EngineWorklet → base(Gain) → [Highpass→Lowpass]            → deep
 *                               → Bandpass 168 Hz             → punch
 *                               → Bandpass 338 Hz             → body
 *                               → Bandpass 860 Hz             → bark
 *                               → Bandpass 1560 Hz            → edge
 *                               → Bandpass 420 → WaveShaper → Bandpass 1320 → air
 *   alle Bänder → master → Compressor → Limiter → Analyser → Ausgang (+ BT-Stream)
 */
import { state, redlineRpm, idleRpm } from '../state/store.js'
import { clamp } from '../util.js'
import { deriveLayerDNA, getSoundModel, toWorkletSoundPacket } from './sound-models.js'
// Worklet-Quelltext als String einbetten → keine separate Datei nötig.
// Dadurch funktioniert die App auch als einzelne, portable HTML-Datei.
import workletSrc from './engine-processor.js?raw'

const now = () => (state.ctx ? state.ctx.currentTime : 0)
let workletReady = false
let sourceMode = 'worklet'
const TAU = Math.PI * 2
const DEFAULT_LAYER_DNA = Object.freeze({
  combustion: 0.5,
  exhaustBody: 0.5,
  intake: 0.4,
  mechanical: 0.25,
  inductionWhine: 0,
  turboAir: 0,
  pop: 0.35,
  smoothness: 0.45,
  orderBlend: 0.5,
  overrunRoughness: 0.45,
})

class FallbackParam {
  constructor(value = 0) {
    this.value = value
  }
  setTargetAtTime(value) {
    this.value = value
  }
  setValueAtTime(value) {
    this.value = value
  }
}

class FallbackEngineNode {
  constructor(ctx) {
    if (!ctx.createScriptProcessor) throw new Error('AudioWorklet wird von diesem Browser nicht unterstützt.')
    this.ctx = ctx
    this.output = ctx.createScriptProcessor(1024, 0, 2)
    this.parameters = new Map(
      [
        ['rpm', 800],
        ['throttle', 0],
        ['overrun', 0],
        ['shift', 0],
        ['cylinders', 8],
        ['subOrder', 0.3],
        ['lumpiness', 0.4],
        ['brightness', 0.3],
        ['rasp', 0.5],
        ['whine', 0],
        ['whineRatio', 26],
        ['turbo', 0],
        ['level', 1],
      ].map(([name, value]) => [name, new FallbackParam(value)]),
    )
    this.model = null
    this.sRpm = 800
    this.sThrottle = 0
    this.sOverrun = 0
    this.sShift = 0
    this.rotPh = 0
    this.cycPh = 0
    this.whPh = 0
    this.fireEnv = 0
    this.nextFireIndex = 0
    this.noise = 0
    this.fastFlow = 0
    this.slowFlow = 0
    this.intakeFast = 0
    this.intakeSlow = 0
    this.mechanicalTick = 0
    this.popEnergy = 0
    this.popPhase = 0
    this.lastPopNoise = 0
    this.layerDNA = { ...DEFAULT_LAYER_DNA }
    this.resonatorState = { exhaust: [], intake: [] }
    this.seed = 22222
    this.port = {
      postMessage: (packet) => {
        if (packet?.type === 'soundModel') this.installSoundModel(packet)
      },
    }
    this.output.onaudioprocess = (event) =>
      this.process(event.outputBuffer.getChannelData(0), event.outputBuffer.getChannelData(1))
  }
  connect(node) {
    return this.output.connect(node)
  }
  disconnect() {
    this.output.disconnect()
  }
  param(name) {
    return this.parameters.get(name)?.value ?? 0
  }
  rand() {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff
    return (this.seed / 0x7fffffff) * 2 - 1
  }
  installSoundModel(packet) {
    this.model = packet
    this.layerDNA = { ...DEFAULT_LAYER_DNA, ...(packet.layerDNA || {}) }
    this.nextFireIndex = 0
    this.resonatorState = {
      exhaust: (packet.resonances?.exhaust || []).map(() => [0, 0, 0, 0, 0]),
      intake: (packet.resonances?.intake || []).map(() => [0, 0, 0, 0, 0]),
    }
  }
  scenarioOrderWeights(throttle, overrun, shift) {
    const blend = this.layerDNA.orderBlend ?? 0.5
    const shiftW = clamp(shift * 1.35, 0, 1)
    const overrunW = clamp(overrun * (0.95 + blend * 0.22), 0, 1) * (1 - shiftW * 0.62)
    const pullW = clamp(throttle * (1 - overrunW * 0.72) * (1 - shiftW * 0.42), 0, 1)
    const idleW = clamp((1 - throttle) * (1 - overrunW * 0.55) * (1 - shiftW), 0, 1)
    const weights = {
      idle: idleW * (0.58 + blend * 0.42),
      pull: pullW + throttle * blend * 0.14,
      overrun: overrunW,
      shift: shiftW,
    }
    const total = Object.values(weights).reduce((sum, value) => sum + value, 0) || 1
    return Object.fromEntries(Object.entries(weights).map(([key, value]) => [key, value / total]))
  }
  renderOrderLayers(frot, bright, nyq, throttle, overrun, shift) {
    const orders = this.model?.orders
    if (!orders) return Math.sin(this.rotPh * 2) * 0.8
    let out = 0
    const weights = this.scenarioOrderWeights(throttle, overrun, shift)
    for (const [scenario, scenarioWeight] of Object.entries(weights)) {
      if (scenarioWeight <= 0.001) continue
      const orderSet = orders[scenario] || orders.pull || orders.idle || []
      for (const [order, gain] of orderSet) {
        const hz = frot * order
        if (hz < nyq) out += Math.sin(this.rotPh * order) * gain * scenarioWeight * (0.42 + 0.58 * bright)
      }
    }
    return out
  }
  triggerFiring(cyclePhase, lump) {
    const cadence = this.model?.fireCadence
    const offsets = cadence?.offsets || []
    const weights = cadence?.weights || []
    if (cyclePhase < this.lastCyclePhase) this.nextFireIndex = 0
    this.lastCyclePhase = cyclePhase
    while (this.nextFireIndex < offsets.length && cyclePhase >= offsets[this.nextFireIndex]) {
      const weight = weights[this.nextFireIndex] || 1
      this.fireEnv = Math.max(this.fireEnv, 0.92 * weight * (1 + lump * 0.16 * this.rand()))
      this.nextFireIndex++
    }
  }
  updateResonanceCoefficients(rf) {
    this.updateResonanceChannel(this.model?.resonances?.exhaust || [], this.resonatorState.exhaust, rf)
    this.updateResonanceChannel(this.model?.resonances?.intake || [], this.resonatorState.intake, rf)
  }
  updateResonanceChannel(resonances, memory, rf) {
    for (let i = 0; i < resonances.length; i++) {
      const resonance = resonances[i]
      const state = memory[i]
      const hz = Math.min(this.ctx.sampleRate * 0.42, resonance[0] * (1 + rf * resonance[3]))
      const bandwidth = Math.max(8, hz / Math.max(0.35, resonance[1]))
      const radius = Math.exp((-Math.PI * bandwidth) / this.ctx.sampleRate)
      state[2] = 2 * radius * Math.cos((TAU * hz) / this.ctx.sampleRate)
      state[3] = radius
      state[4] = (1 - radius) * resonance[2]
    }
  }
  processResonance(input, resonances, memory, gain) {
    let out = 0
    for (let i = 0; i < resonances.length; i++) {
      const state = memory[i]
      const radius = state[3]
      const y = input * state[4] + state[2] * state[0] - radius * radius * state[1]
      state[1] = state[0]
      state[0] = y
      out += y
    }
    return out * gain
  }
  process(channel, rightChannel = null) {
    const sr = this.ctx.sampleRate
    const rpmTarget = Math.max(0, this.param('rpm'))
    const throttleTarget = clamp(this.param('throttle'), 0, 1)
    const overrunTarget = clamp(this.param('overrun'), 0, 1)
    const shiftTarget = clamp(this.param('shift'), 0, 1)
    const cylinders = Math.max(1, Math.round(this.param('cylinders')))
    const subOrder = this.param('subOrder')
    const lump = this.param('lumpiness')
    const brite = this.param('brightness')
    const rasp = this.param('rasp')
    const whine = this.param('whine')
    const whineRatio = this.param('whineRatio')
    const turbo = this.param('turbo')
    const level = this.param('level')
    const nyq = sr * 0.45
    const dna = this.layerDNA

    for (let i = 0; i < channel.length; i++) {
      this.sRpm += (rpmTarget - this.sRpm) * 0.0025
      this.sThrottle += (throttleTarget - this.sThrottle) * 0.01
      this.sOverrun += (overrunTarget - this.sOverrun) * 0.01
      this.sShift += (shiftTarget - this.sShift) * 0.02

      const rpm = this.sRpm
      const throttle = this.sThrottle
      const overrun = this.sOverrun
      const shift = this.sShift
      const rf = clamp((rpm - 700) / 6500, 0, 1)
      if (i === 0) this.updateResonanceCoefficients(rf)

      const frot = rpm / 60
      const fcyc = frot / 2
      this.rotPh = (this.rotPh + (TAU * frot) / sr) % TAU
      this.cycPh += (TAU * fcyc) / sr
      if (this.cycPh >= TAU) this.cycPh -= TAU
      this.triggerFiring(this.cycPh / TAU, lump)
      this.fireEnv *= 0.992

      let sample = 0
      const bright = 0.22 + 0.7 * throttle + brite * 0.6
      sample += this.renderOrderLayers(frot, bright, nyq, throttle, overrun, shift)

      const maxK = Math.min(24, Math.floor(nyq / Math.max(1, fcyc)))
      for (let k = 1; k <= maxK; k++) {
        let gain = (1 / (1 + 0.16 * k)) * Math.pow(bright, k / cylinders)
        if (k === 1) gain += subOrder * 0.75
        if (k === 2) gain += subOrder * 0.42
        sample += Math.sin(this.cycPh * k) * gain * (0.22 + dna.combustion * 0.34)
      }

      const raw = this.rand()
      this.fastFlow += (raw - this.fastFlow) * 0.18
      this.slowFlow += (this.fastFlow - this.slowFlow) * 0.025
      const combustion = Math.tanh(this.fireEnv * (1.2 + throttle * 0.7 + overrun * 0.34))
      const exhaustFlow = (this.fastFlow - this.slowFlow) * (0.16 + throttle * 0.34 + overrun * 0.28)
      const intakeRaw = this.rand()
      this.intakeFast += (intakeRaw - this.intakeFast) * (0.08 + throttle * 0.08)
      this.intakeSlow += (this.intakeFast - this.intakeSlow) * 0.014
      const intakePulse = 0.55 + 0.45 * Math.sin(this.rotPh * Math.max(1, cylinders / 2))
      const intake =
        (this.intakeFast - this.intakeSlow) * intakePulse * (0.035 + throttle * 0.15) * (0.55 + dna.intake)
      const valveEdge = Math.max(0, Math.sin(this.rotPh * cylinders))
      this.mechanicalTick += (Math.pow(valveEdge, 18) - this.mechanicalTick) * 0.28
      const mechanical = (this.mechanicalTick - 0.03) * (0.01 + rf * 0.025) * dna.mechanical
      sample += combustion * (0.18 + dna.combustion * 0.22 + subOrder * 0.16 + throttle * 0.16)
      sample += exhaustFlow * (0.72 + dna.exhaustBody * 0.9) + intake + mechanical
      sample += this.processResonance(
        sample,
        this.model?.resonances?.exhaust || [],
        this.resonatorState.exhaust,
        0.34 + dna.exhaustBody * 0.34,
      )
      sample += this.processResonance(
        intake,
        this.model?.resonances?.intake || [],
        this.resonatorState.intake,
        0.2 + dna.intake * 0.32,
      )

      const popDrive = overrun * overrun * dna.pop * (0.35 + dna.overrunRoughness)
      const popGate = popDrive * Math.max(0.08, this.fireEnv) * (0.00016 + dna.overrunRoughness * 0.0003)
      if ((this.rand() + 1) * 0.5 < popGate) this.popEnergy = Math.max(this.popEnergy, 0.52 + dna.pop * 0.56)
      this.popEnergy *= 0.982 - dna.smoothness * 0.004
      this.popPhase = (this.popPhase + (TAU * (58 + rpm * 0.012)) / sr) % TAU
      const popNoise = this.rand()
      const popCrackle =
        (popNoise - this.lastPopNoise) * this.popEnergy * (0.18 + brite * 0.08 + dna.overrunRoughness * 0.14)
      this.lastPopNoise = popNoise
      sample += popCrackle + Math.sin(this.popPhase) * this.popEnergy * (0.04 + dna.pop * 0.05)

      if (whine > 0) {
        const hz = frot * whineRatio
        if (hz < nyq) {
          this.whPh = (this.whPh + (TAU * hz) / sr) % TAU
          sample +=
            whine *
            (0.06 + dna.inductionWhine * 0.5) *
            (0.25 + 0.75 * rf) *
            (Math.sin(this.whPh) + 0.25 * Math.sin(this.whPh * 2))
        }
      }
      if (turbo > 0) {
        this.noise += (this.rand() - this.noise) * 0.12
        sample += this.noise * turbo * (0.06 + dna.turboAir * 0.42 + throttle * 0.3) * (0.3 + rf * 0.7)
      }

      const drive = 1 + rasp * 2 + throttle * 1.4
      const mono = (Math.tanh(sample * drive) / Math.tanh(drive)) * level * 0.56
      const side =
        (Math.sin(this.rotPh * 0.5 + this.cycPh * 0.17) * 0.018 +
          exhaustFlow * 0.11 -
          intake * 0.08 +
          mechanical * 0.12) *
        (0.35 + brite * 0.22 + turbo * 0.08)
      channel[i] = clamp(mono + side, -0.92, 0.92)
      if (rightChannel) rightChannel[i] = clamp(mono - side, -0.92, 0.92)
    }
  }
}

/** tanh-Sättigungskurve für Limiter / Excitation. */
function curve(d) {
  const n = 2048,
    arr = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1
    arr[i] = Math.tanh(x * d)
  }
  return arr
}

/** Baut den Master-Bus (Compressor → Limiter → Analyser → Ausgang + BT-Stream). */
export function makeGraph() {
  const c = state.ctx
  state.master = c.createGain()
  state.master.gain.value = state.mix.master

  state.comp = c.createDynamicsCompressor()
  state.comp.threshold.value = -20
  state.comp.knee.value = 18
  state.comp.ratio.value = 4.2
  state.comp.attack.value = 0.006
  state.comp.release.value = 0.16

  state.limiter = c.createWaveShaper()
  state.limiter.curve = curve(1.7)

  state.analyser = c.createAnalyser()
  state.analyser.fftSize = 64

  state.master.connect(state.comp).connect(state.limiter).connect(state.analyser).connect(c.destination)

  state.dest = c.createMediaStreamDestination()
  state.limiter.connect(state.dest)

  state.spatial = {
    outputMode: 'spatial-stereo',
    maxChannels: c.destination.maxChannelCount || 2,
  }
  makeEventBus()
}

/** Erzeugt einen Biquad-Filter und hängt ihn an `src`. */
function band(src, type, f, q) {
  const n = state.ctx.createBiquadFilter()
  n.type = type
  n.frequency.value = f
  if (q) n.Q.value = q
  src.connect(n)
  return n
}

function spatialLayer(src, { pan = 0, delayMs = 0, gain = 1 } = {}) {
  const c = state.ctx
  const delay = c.createDelay ? c.createDelay(0.04) : null
  const level = c.createGain()
  const panner = c.createStereoPanner ? c.createStereoPanner() : null

  level.gain.value = gain
  if (delay) delay.delayTime.value = Math.max(0, delayMs / 1000)
  if (panner) panner.pan.value = pan

  if (delay) src.connect(delay).connect(level)
  else src.connect(level)

  if (panner) level.connect(panner).connect(state.master)
  else level.connect(state.master)

  return { gain: level, delay, pan: panner }
}

function makeEventBus() {
  const c = state.ctx
  const bus = c.createGain()
  bus.gain.value = 0.54
  const layer = spatialLayer(bus, { pan: 0.18, delayMs: 5 })
  state.eventBus = bus
  state.spatial.event = layer
}

/** Baut den kompletten Bandsplit für eine Quelle und gibt {base, bands} zurück. */
function makeBandSplit(source) {
  const c = state.ctx
  const base = c.createGain()
  base.gain.value = 0
  source.connect(base)

  const bands = {
    core: c.createGain(),
    deep: c.createGain(),
    punch: c.createGain(),
    body: c.createGain(),
    bark: c.createGain(),
    edge: c.createGain(),
    air: c.createGain(),
  }
  const spatial = {}

  const hp = band(base, 'highpass', 34, 0.68)
  const lp = c.createBiquadFilter()
  lp.type = 'lowpass'
  lp.frequency.value = 118
  lp.Q.value = 0.58
  hp.connect(lp)
  spatial.deep = spatialLayer(lp, { pan: 0, delayMs: 0 })
  bands.deep = spatial.deep.gain

  const coreHp = band(base, 'highpass', 44, 0.62)
  const coreLp = c.createBiquadFilter()
  coreLp.type = 'lowpass'
  coreLp.frequency.value = 2600
  coreLp.Q.value = 0.48
  coreHp.connect(coreLp)
  spatial.core = spatialLayer(coreLp, { pan: 0, delayMs: 0.6, gain: 0.22 })
  bands.core = spatial.core.gain

  spatial.punch = spatialLayer(band(base, 'bandpass', 168, 0.82), { pan: -0.08, delayMs: 1.5 })
  bands.punch = spatial.punch.gain
  spatial.body = spatialLayer(band(base, 'bandpass', 338, 0.78), { pan: 0.06, delayMs: 2 })
  bands.body = spatial.body.gain
  spatial.bark = spatialLayer(band(base, 'bandpass', 860, 0.95), { pan: 0.24, delayMs: 4 })
  bands.bark = spatial.bark.gain
  spatial.edge = spatialLayer(band(base, 'bandpass', 1560, 0.88), { pan: -0.38, delayMs: 6 })
  bands.edge = spatial.edge.gain

  const exc = c.createWaveShaper()
  exc.curve = curve(3.6)
  const exIn = band(base, 'bandpass', 420, 0.75)
  exIn.connect(exc)
  const exOut = c.createBiquadFilter()
  exOut.type = 'bandpass'
  exOut.frequency.value = 1320
  exOut.Q.value = 0.92
  exc.connect(exOut)
  spatial.air = spatialLayer(exOut, { pan: 0.44, delayMs: 9 })
  bands.air = spatial.air.gain

  return { base, bands, spatial }
}

/** Überträgt die motorspezifischen Synth-Parameter des Profils auf den Worklet. */
function applyProfileParams(node, profile) {
  const soundModel = profile.soundModelId ? getSoundModel(profile.soundModelId) : null
  const engine = soundModel?.engine
  const induction = soundModel?.forcedInduction
  const t = now()
  const set = (name, v) => {
    const p = node.parameters.get(name)
    if (p) p.setValueAtTime(v, t)
  }
  set('cylinders', engine?.cylinders ?? profile.cylinders ?? 8)
  set('subOrder', profile.subOrder ?? 0.3)
  set('lumpiness', profile.lumpiness ?? 0.35)
  set('brightness', profile.brightness ?? 0.3)
  set('rasp', profile.rasp ?? 0.5)
  set('whine', induction?.type === 'supercharger' ? induction.gain : (profile.whine ?? 0))
  set('whineRatio', induction?.whineRatio ?? profile.whineRatio ?? 26)
  set('turbo', induction?.type?.includes('turbo') ? induction.gain : (profile.turbo ?? 0))
  if (soundModel && node.port) node.port.postMessage(toWorkletSoundPacket(soundModel))
}

function activeLayerDNA() {
  try {
    return deriveLayerDNA(getSoundModel(state.profile?.soundModelId))
  } catch {
    return DEFAULT_LAYER_DNA
  }
}

/** Stoppt und verwirft die laufende Synthese-Quelle. */
export function stopSources() {
  try {
    state.sources.forEach((s) => {
      try {
        s.disconnect()
      } catch {}
    })
    state.sources = []
    state.strips = []
  } catch {}
}

/** Baut die prozedurale Motorquelle für das aktive Profil. */
export function buildSources() {
  try {
    stopSources()
    let node
    if (workletReady) {
      const WorkletNode = window.AudioWorkletNode || globalThis.AudioWorkletNode
      node = new WorkletNode(state.ctx, 'engine-processor', {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      })
      sourceMode = 'worklet'
    } else {
      node = new FallbackEngineNode(state.ctx)
      sourceMode = 'script-processor'
    }
    applyProfileParams(node, state.profile)
    const { base, bands, spatial } = makeBandSplit(node)
    state.sources.push(node)
    state.strips.push({ node, base, bands, spatial })
    state.engine = true
    state.audioMode = sourceMode
  } catch (e) {
    console.error('[REVX] buildSources:', e.message)
  }
}

/** Setzt einen Gain weich (setTargetAtTime), mit Fallback. */
function setG(g, v, t = 0.045) {
  try {
    g.gain.setTargetAtTime(Math.max(0, v), now(), t)
  } catch {
    g.gain.value = Math.max(0, v)
  }
}

/** Setzt einen AudioParam weich. */
function setP(p, v, t = 0.03) {
  try {
    p.setTargetAtTime(v, now(), t)
  } catch {
    p.value = v
  }
}

/** Interpoliert die RPM-abhängige Gesamtlautstärke aus den 8 Bändern. */
export function rpmBandVol(rpm) {
  const idle = idleRpm(),
    red = redlineRpm()
  const x = clamp((rpm - idle) / (red - idle), 0, 1) * 7
  const i = Math.floor(x),
    f = x - i
  const a = state.rpmBands[clamp(i, 0, 7)],
    b = state.rpmBands[clamp(i + 1, 0, 7)]
  return a + (b - a) * f
}

/** Pro-Frame-Mischung: füttert Worklet-Parameter und Band-Gains. */
export function updateAudio() {
  if (!state.ctx || !state.engine) return
  try {
    const idle = idleRpm(),
      red = redlineRpm()
    const rn = clamp((state.rpm - idle) / (red - idle), 0, 1)
    const th = state.throttle,
      ov = state.overrun || 0,
      shift = state.shift
    const sensorA = state.sensor ? clamp(state.sensorAccel || 0, 0, 1) : 0
    const sensorB = state.sensor ? clamp(state.sensorBrake || 0, 0, 1) : 0
    const gPunch = clamp(state.gPunch || 0, 0, 1)
    const off = Math.max(ov, (1 - th) * clamp((state.rpm - 1200) / 3200, 0, 1), sensorB * 0.62)
    const bandVol = rpmBandVol(state.rpm)
    const dna = activeLayerDNA()
    const quietCabin = state.settings.airfix ? 1 : 0
    const openWindow = state.activeMixPreset === 'open' ? 1 : 0
    const extreme = state.activeMixPreset === 'beast' ? 1 : 0
    const deepTone = clamp(
      0.92 + dna.exhaustBody * 0.42 - dna.intake * 0.1 - dna.mechanical * 0.08,
      0.78,
      1.38,
    )
    const punchTone = clamp(0.82 + dna.combustion * 0.24 + dna.pop * 0.14 - dna.smoothness * 0.08, 0.7, 1.24)
    const bodyTone = clamp(0.8 + dna.exhaustBody * 0.26 + dna.smoothness * 0.08, 0.72, 1.22)
    const barkTone = clamp(0.72 + dna.combustion * 0.2 + dna.pop * 0.18 + dna.intake * 0.12, 0.7, 1.28)
    const edgeTone = clamp(
      0.42 + dna.intake * 0.26 + dna.mechanical * 0.28 + dna.inductionWhine * 0.14 - dna.smoothness * 0.08,
      0.36,
      1.02,
    )
    const airTone = clamp(
      0.24 +
        dna.intake * 0.42 +
        dna.turboAir * 0.38 +
        dna.inductionWhine * 0.48 +
        dna.mechanical * 0.28 -
        dna.exhaustBody * 0.18,
      0.08,
      0.98,
    )
    const airDnaContrast = clamp(
      0.54 + dna.turboAir * 0.72 + dna.inductionWhine * 0.68 - dna.exhaustBody * 0.12,
      0.32,
      1.28,
    )

    state.master.gain.setTargetAtTime(state.mix.master * bandVol * (1 + sensorA * 0.06), now(), 0.03)

    state.strips.forEach((st) => {
      // Motorquelle steuern (Frequenz ergibt sich im Worklet aus der RPM).
      // throttle/overrun kommen bereits geglättet aus der Physik – nicht erneut
      // mit rohem Sensorwert überlagern (das verursachte das „Springen").
      setP(st.node.parameters.get('rpm'), state.rpm, 0.03)
      setP(st.node.parameters.get('throttle'), clamp(th, 0, 1), 0.07)
      setP(st.node.parameters.get('overrun'), off, 0.06)
      setP(st.node.parameters.get('shift'), shift, 0.025)

      // Lautstärke-Hüllkurve der Quelle (Lastabhängigkeit)
      const baseGain =
        state.mix.normalizer *
        (0.42 + 0.55 * th + 0.12 * rn + 0.12 * off + 0.1 * sensorA + 0.08 * gPunch + openWindow * 0.04)
      setG(st.base, baseGain, 0.05)

      // Bänder formen den Klang (wie zuvor)
      setG(st.bands.core, state.mix.body * (0.24 + 0.22 * th + 0.12 * off + 0.08 * rn), 0.06)
      setG(
        st.bands.deep,
        state.mix.deep *
          deepTone *
          (1 - state.mix.subGuard * 0.42) *
          (1 - rn * 0.34) *
          (1 + quietCabin * 0.22 + openWindow * 0.18),
        0.06,
      )
      setG(
        st.bands.punch,
        state.mix.punch * punchTone * (0.76 + 0.42 * th + 0.18 * shift + 0.22 * sensorA + 0.16 * gPunch),
        0.045,
      )
      setG(
        st.bands.body,
        state.mix.body * bodyTone * (0.98 + 0.3 * rn + 0.12 * off + 0.1 * sensorA + openWindow * 0.14),
        0.05,
      )
      setG(
        st.bands.bark,
        state.mix.bark * barkTone * (0.3 + 0.72 * rn + 0.34 * th + 0.2 * off + 0.12 * sensorA),
        0.045,
      )
      setG(
        st.bands.edge,
        state.mix.edge *
          edgeTone *
          (rn > 0.24 ? 0.11 + rn : 0.06) *
          (0.22 + 0.58 * th + 0.16 * shift + 0.08 * sensorA) *
          (0.68 - openWindow * 0.06 + extreme * 0.18),
        0.04,
      )
      setG(
        st.bands.air,
        state.mix.airfix *
          state.mix.edge *
          airTone *
          state.settings.airfix *
          airDnaContrast *
          (0.1 + 0.8 * rn) *
          (0.14 + 0.54 * th + 0.12 * off) *
          (0.58 - openWindow * 0.08 + extreme * 0.22),
        0.04,
      )
    })
  } catch {}
}

/** Aktualisiert die Synth-Parameter nach Profilwechsel (ohne Neuaufbau). */
export function refreshProfileParams() {
  state.strips.forEach((st) => applyProfileParams(st.node, state.profile))
}

/** Stellt sicher, dass Context + Graph + Worklet bereit sind. */
export async function ensureContext() {
  if (!state.ctx) {
    const AC = window.AudioContext || window.webkitAudioContext
    if (!AC) throw new Error('Web Audio wird von diesem Browser nicht unterstützt.')
    state.ctx = new AC({ latencyHint: 'interactive' })
    await state.ctx.resume()
    makeGraph()
    if (state.ctx.audioWorklet && (window.AudioWorkletNode || globalThis.AudioWorkletNode)) {
      const blobUrl = URL.createObjectURL(new Blob([workletSrc], { type: 'application/javascript' }))
      try {
        await state.ctx.audioWorklet.addModule(blobUrl)
        workletReady = true
        sourceMode = 'worklet'
      } catch (e) {
        if (!state.ctx.createScriptProcessor) throw e
        workletReady = false
        sourceMode = 'script-processor'
        state.audioMode = sourceMode
        console.warn('[REVX] AudioWorklet konnte nicht starten, nutze ScriptProcessor-Fallback.', e)
      } finally {
        URL.revokeObjectURL(blobUrl)
      }
    } else if (state.ctx.createScriptProcessor) {
      workletReady = false
      sourceMode = 'script-processor'
      state.audioMode = sourceMode
      console.warn('[REVX] AudioWorklet fehlt, nutze lokalen ScriptProcessor-Fallback.')
    } else {
      throw new Error('Web Audio Synthese wird von diesem Browser nicht unterstützt.')
    }
  }
  await state.ctx.resume()
}
